/**
 * 视频管理 API
 *
 * VideoProject 项目级 CRUD 已废弃（Task 9.2 / D8）；
 * 文件夹 → project.ts；条目 → work-item.ts；业务操作入参为 work_item_id。
 */
import type {
  FileUploadResponse,
  PaginatedResponse,
  PaginationParams,
  ProjectStatus,
  TaskAssignmentType,
  TaskType,
  UserSummary,
} from '@/api/common-types'
import { request } from '@/shared/api/request'

export { ProjectStatus, TaskAssignmentType, TaskType }

/** @deprecated 请使用 WorkItem */
export interface VideoProject {
  id: number
  title: string
  file_storage_id?: number
  video_file_storage_id?: number
  original_subtitle_storage_id?: number
  translated_subtitle_storage_id?: number
  audio_file_storage_id?: number
  video_url?: string
  video_size?: number
  video_duration?: number
  original_language?: string
  target_languages?: string[]
  subtitle_url?: string
  status: ProjectStatus | string
  task_type?: TaskAssignmentType | TaskType
  creator_id: number
  assignee_id?: number
  character_count?: number
  retention_days?: number
  expire_at?: string
  created_at: string
  updated_at: string
  creator?: UserSummary
  assignee?: UserSummary
  file_url?: string
  video_file_path?: string
  audio_file_path?: string
}

/** @deprecated */
export interface VideoProjectCreate {
  title: string
  original_language: string
  description?: string
}

/** @deprecated */
export interface VideoProjectUpdate {
  title?: string
  original_language?: string
  description?: string
  status?: ProjectStatus
}

/** @deprecated */
export interface VideoProjectListResponse extends PaginatedResponse<VideoProject> {
  total_pages: number
}

/** @deprecated */
export interface VideoProjectQueryParams extends PaginationParams {
  status?: ProjectStatus
  keyword?: string
  creator_id?: number
  assignee_id?: number
  date_range?: [string, string]
}

export interface VideoProcessingStatus {
  work_item_id?: number
  project_id?: number
  status: string
  progress: number
  message?: string
  current_step?: string
  total_steps?: number
  completed_steps?: number
}

function deprecatedVideoProjectCrud(name: string): never {
  throw new Error(
    `${name} 已废弃：请使用 projectApi.createProject + workItemApi.createWorkItem。`,
  )
}

export const videoApi = {
  /** @deprecated */
  createProject: (_data: VideoProjectCreate): Promise<VideoProject> => {
    deprecatedVideoProjectCrud('createProject')
  },

  /** @deprecated */
  getProjects: (_params?: VideoProjectQueryParams): Promise<VideoProjectListResponse> => {
    deprecatedVideoProjectCrud('getProjects')
  },

  /** @deprecated 请使用 getWorkItem */
  getProject: (_projectId: number): Promise<VideoProject> => {
    deprecatedVideoProjectCrud('getProject')
  },

  /** @deprecated */
  updateProject: (_projectId: number, _data: VideoProjectUpdate): Promise<VideoProject> => {
    deprecatedVideoProjectCrud('updateProject')
  },

  uploadVideo: (
    workItemId: number,
    file: File,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal,
  ) => {
    const formData = new FormData()
    formData.append('file', file)

    return request.post<FileUploadResponse>(
      `/videos/work-items/${workItemId}/upload-video`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000,
        signal,
        onUploadProgress: (progressEvent: { total?: number; loaded: number }) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      },
    )
  },

  uploadSubtitle: (
    workItemId: number,
    file: File,
    language: string,
    onProgress?: (progress: number) => void,
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('language', language)

    return request.post<FileUploadResponse>(
      `/videos/work-items/${workItemId}/upload-subtitle`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
        onUploadProgress: (progressEvent: { total?: number; loaded: number }) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            onProgress(progress)
          }
        },
      },
    )
  },

  deleteSubtitle: (workItemId: number) =>
    request.delete(`/videos/work-items/${workItemId}/subtitle`),

  deleteProjectFiles: (workItemId: number) =>
    request.delete(`/videos/work-items/${workItemId}/files`),

  getProcessingStatus: (workItemId: number) =>
    request.get<VideoProcessingStatus>(
      `/videos/work-items/${workItemId}/extraction-progress`,
    ),

  exportProject: (workItemId: number, exportRequest: unknown) =>
    request.post<unknown>(`/videos/work-items/${workItemId}/export`, exportRequest),

  getExportProgress: (workItemId: number, exportId: string) =>
    request.get<unknown>(`/videos/work-items/${workItemId}/exports/${exportId}/progress`),

  downloadExport: (workItemId: number, exportId: string) =>
    request.get(`/videos/work-items/${workItemId}/exports/${exportId}/download`, {
      responseType: 'blob',
    }),

  deleteExport: (workItemId: number, exportId: string) =>
    request.delete(`/videos/work-items/${workItemId}/exports/${exportId}`),

  cleanupExpiredExports: () => request.post('/videos/exports/cleanup'),

  generateReport: (workItemId: number, reportName?: string) =>
    request.post(`/videos/work-items/${workItemId}/generate-report`, null, {
      params: reportName ? { report_name: reportName } : undefined,
    }),

  listReports: (workItemId: number) =>
    request.get(`/videos/work-items/${workItemId}/reports`),
}

export const createVideoProject = videoApi.createProject
export const getVideoProjects = videoApi.getProjects
export const getVideoProject = videoApi.getProject
export const updateVideoProject = videoApi.updateProject
export const uploadVideoFile = videoApi.uploadVideo
export const uploadSubtitleFile = videoApi.uploadSubtitle
export const deleteSubtitleFile = videoApi.deleteSubtitle
export const deleteVideoProjectFiles = videoApi.deleteProjectFiles
export const getVideoProcessingStatus = videoApi.getProcessingStatus
export const exportVideoProject = videoApi.exportProject
export const getVideoExportProgress = videoApi.getExportProgress
export const downloadVideoExport = videoApi.downloadExport
export const deleteVideoExport = videoApi.deleteExport
export const cleanupExpiredVideoExports = videoApi.cleanupExpiredExports

export default videoApi
